import * as i0 from '@angular/core';
import { InjectionToken, Injectable, inject, Injector, PLATFORM_ID, Renderer2, ElementRef, input, effect, Component, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { switchMap, from, of, map, BehaviorSubject } from 'rxjs';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
const SvgOptionsToken = new InjectionToken('__SvgOptionsToken__', {
  providedIn: 'root',
  factory: () => ({
    url: () => '',
    defaultSize: undefined,
    suspenseSvgString: undefined
  })
});
let SvgLoadStrategy = /*#__PURE__*/(() => {
  class SvgLoadStrategy {
    static {
      this.ɵfac = function SvgLoadStrategy_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SvgLoadStrategy)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SvgLoadStrategy,
        factory: SvgLoadStrategy.ɵfac
      });
    }
  }
  return SvgLoadStrategy;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// eslint-disable-next-line @typescript-eslint/ban-types
function getZoneUnPatchedApi(name, elem) {
  return elem['__zone_symbol__' + name] !== undefined ? elem['__zone_symbol__' + name] : elem[name];
}
let SvgLoadStrategyImpl = /*#__PURE__*/(() => {
  class SvgLoadStrategyImpl {
    constructor() {
      this.fetch = getZoneUnPatchedApi('fetch', window);
    }
    load(url$) {
      return url$.pipe(switchMap(url => {
        return from(fetch(url).then(res => !res.ok ? '' : res.text()));
      }));
    }
    config(url) {
      return of(url);
    }
    static {
      this.ɵfac = function SvgLoadStrategyImpl_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SvgLoadStrategyImpl)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SvgLoadStrategyImpl,
        factory: SvgLoadStrategyImpl.ɵfac
      });
    }
  }
  return SvgLoadStrategyImpl;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const suspenseSvg = `<svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <circle cx="15.5" cy="9.5" r="1.5" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path
        d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm0-6c-2.33 0-4.32 1.45-5.12 3.5h1.67c.69-1.19 1.97-2 3.45-2s2.75.81 3.45 2h1.67c-.8-2.05-2.79-3.5-5.12-3.5z"
      />
    </svg>`;
function createDomParser(document) {
  const e = document.createElement('DIV');
  return s => {
    e && (e.innerHTML = s);
    return e.firstChild;
  };
}
function styleDomCacheForPerformance(el) {
  /**
   * reduce paint area with width/height 0 and overflow hidden
   * fixed position of -2000px to always have it offscreen and outside any native trigger (viewport observer in content visibilities)
   * contain:content to leverage css perf features for older browsers not supporting content-visibility   * : auto to exclude it completely from styles recalculation
   */
  el.setAttribute('style', `
      overflow: hidden;
      width: 0px;
      height: 0px;
      position: fixed;
      bottom: -2000px;
      contain: content;
      content-visibility: auto;
    `);
  return el;
}
let SvgRegistry = /*#__PURE__*/(() => {
  class SvgRegistry {
    constructor() {
      this.document = inject(DOCUMENT);
      this.svgOptions = inject(SvgOptionsToken);
      this.svgLoadStrategy = inject(SvgLoadStrategy, {
        optional: true
      }) // custom strategy if provided
      || new SvgLoadStrategyImpl(); // default strategy
      this.domParser = createDomParser(this.document);
      this.svgDomCache = (() => {
        // The DOM cache could be already created on SSR or due to multiple instances of the registry
        const domCache = this.document.getElementById('svg-cache') || this.domParser('<div id="svg-cache"></div>');
        styleDomCacheForPerformance(domCache);
        this.document.body.appendChild(domCache);
        return domCache;
      })();
      this._svgHrefCache = new Map();
      this._cachedSvgs = new Set();
      this.defaultSize = this.svgOptions?.defaultSize || '24';
      this._defaultViewBox = `0 0 ${this.defaultSize} ${this.defaultSize}`;
      this.url = name => this.svgLoadStrategy.config(this.svgOptions.url(name));
      this.fetchSvg = svgName => {
        const svgId = this.svgId(svgName);
        // if the svg is already fetched we return early
        if (this._cachedSvgs.has(svgId)) {
          return;
        }
        this._cachedSvgs.add(svgId);
        // trigger fetch
        this.svgLoadStrategy.load(this.url(svgName)).subscribe({
          next: body => this.cacheSvgInDOM(svgId, body),
          error: console.error
        });
      };
      // configure suspense svg
      const suspenseSvgId = this.svgId('suspense');
      if (!this._cachedSvgs.has(suspenseSvgId)) {
        this.cacheSvgInDOM(suspenseSvgId, this.svgOptions.suspenseSvgString || suspenseSvg);
      }
      this.hydrateFromDom();
    }
    hydrateFromDom() {
      // hydrate DOM cache
      Array.from(this.svgDomCache.children).forEach(i => {
        // add to fetchedSvgs
        this._cachedSvgs.add(i.id);
        // i.
        // publish to components and render it
        this.getSvgSubject(i.id).next({
          name: i.id,
          viewBox: i.getAttribute('viewBox') || this._defaultViewBox
        });
      });
    }
    isSvgCached(name) {
      return this._cachedSvgs.has(this.svgId(name));
    }
    svgCache$(name) {
      // start by displaying the suspense svg immediately
      return this.getSvgSubject(this.svgId(name)).pipe(map(svg => ({
        name: `#${svg.name}`,
        viewBox: svg.viewBox
      })));
    }
    cacheSvgInDOM(svgId, svgString) {
      // create HTML
      const svgElem = this.domParser(svgString);
      // parse the svg string and get a viewBox
      const viewBox = svgString.match(/viewBox="([^"]*)"/);
      // the SVG element needs to be accessible over a href and end with a specific anchor to select the element by id
      svgElem?.setAttribute && svgElem.setAttribute('id', svgId);
      this.svgDomCache.appendChild(svgElem);
      // notify subscribers about change
      this.getSvgSubject(svgId).next({
        name: svgId,
        viewBox: viewBox?.[1] || this._defaultViewBox
      });
    }
    /*
    private removeSvgInDOM(svgId: string): void {
      this.svgDomCache.querySelector(`#${svgId}`)?.remove();
    }*/
    getSvgSubject(svgId) {
      if (!this._svgHrefCache.has(svgId)) {
        this._svgHrefCache.set(svgId, new BehaviorSubject({
          name: this.svgId('suspense'),
          viewBox: this._defaultViewBox
        }));
      }
      return this._svgHrefCache.get(svgId);
    }
    // pattern has to be `<svgName>`
    svgId(name) {
      return `${name}`;
    }
    static {
      this.ɵfac = function SvgRegistry_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SvgRegistry)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SvgRegistry,
        factory: SvgRegistry.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SvgRegistry;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/* eslint-disable @typescript-eslint/ban-types */
/**
 * getZoneUnPatchedApi
 *
 * @description
 *
 * This function returns the zone un-patched API for the a specific Browser API.
 * If no element is passed the window is used instead
 *
 * @param name {string} - The name of the API to check.
 * @param elem {any} - The elem to get un-patched API from.
 * @return {Function} - The zone un-patched API in question.
 *
 */
const addEventListener = (elem, name, listener) => getZoneUnPatchedApi('addEventListener', elem).bind(elem)(name, listener);
let element = undefined;
function createGetImgFn(renderer) {
  if (element === undefined) {
    element = renderer.createElement('img');
    element.setAttribute('style', 'display: none; contain: content; content-visibility: auto;');
    element.setAttribute('loading', 'lazy');
    element.setAttribute('alt', 'loading helper');
    element.setAttribute('width', '0');
    element.setAttribute('height', '0');
  }
  return src => {
    const e = element.cloneNode(true);
    e.setAttribute('src', src);
    return e;
  };
}
/**
 * ngx-fast-svg enables lazy loading features of the browser for SVG.
 * It also manages the caching of SVG's in the DOM for multiple usage and different stylings
 * This component also Supports lazy loading with SSR and http transfer cache
 * */
let FastSvgComponent = /*#__PURE__*/(() => {
  class FastSvgComponent {
    constructor() {
      this.injector = inject(Injector);
      this.platform = inject(PLATFORM_ID);
      this.renderer = inject(Renderer2);
      this.registry = inject(SvgRegistry);
      this.element = inject(ElementRef);
      this.getImg = createGetImgFn(this.renderer);
      this.name = input('');
      this.size = input(this.registry.defaultSize);
      this.width = input('');
      this.height = input('');
      this.#url = toSignal(toObservable(this.name).pipe(switchMap(name => {
        return this.registry.url(name);
      })));
      // When the browser loaded the svg resource we trigger the caching mechanism
      // re-fetch -> cache-hit -> get SVG -> cache in DOM
      this.loadedListener = () => {
        this.registry.fetchSvg(this.name());
      };
    }
    #url;
    ngAfterViewInit() {
      // Setup view refs and init them
      const elem = this.element.nativeElement;
      const svg = elem.querySelector('svg');
      effect(() => {
        // apply size
        if (this.size() && svg) {
          // We apply fixed dimensions
          // Additionally to SEO rules, to avoid any scroll flicker caused by `content-visibility:auto` defined in component styles
          svg.setAttribute('width', this.width() || this.size());
          svg.setAttribute('height', this.height() || this.width() || this.size());
        }
      }, {
        injector: this.injector
      });
      effect(onCleanup => {
        const name = this.name();
        if (!name) {
          throw new Error('svg component needs a name to operate');
        }
        let img = null;
        // if svg is not in cache we append
        if (!this.registry.isSvgCached(name)) {
          /**
            CSR - Browser native lazy loading hack
                 We use an img element here to leverage the browsers native features:
            - lazy loading (loading="lazy") to only load the svg that are actually visible
            - priority hints to down prioritize the fetch to avoid delaying the LCP
                 While the SVG is loading we display a fallback SVG.
            After the image is loaded we remove it from the DOM. (IMG load event)
            When the new svg arrives we append it to the template.
                 Note:
            - the image is styled with display none. this prevents any loading of the resource ever.
            on component bootstrap we decide what we want to do. when we remove display none it performs the browser native behavior
            - the image has 0 height and with and containment as well as content-visibility to reduce any performance impact
                      Edge cases:
            - only resources that are not loaded in the current session of the browser will get lazy loaded (same URL to trigger loading is not possible)
            - already loaded resources will get emitted from the cache immediately, even if loading is set to lazy :o
            - the image needs to have display other than none
          */
          const url = this.#url();
          if (url) {
            const i = this.getImg(url);
            this.renderer.appendChild(this.element.nativeElement, i);
            // get img
            img = elem.querySelector('img');
            addEventListener(img, 'load', this.loadedListener);
          }
        }
        // Listen to svg changes
        // This potentially could already receive the svg from the cache and drop the img from the DOM before it gets activated for lazy loading.
        // NOTICE:
        // If the svg is already cached the following code will execute synchronously. This gives us the chance to add
        const sub = this.registry.svgCache$(name).subscribe(cache => {
          // The first child is the `use` tag. The value of href gets displayed as SVG
          svg.children[0].setAttribute('href', cache.name);
          svg.setAttribute('viewBox', cache.viewBox);
          // early exit no image
          if (!img) return;
          // If the img is present
          // and the name in included in the href (svg is fully loaded, not only the suspense svg)
          // Remove the element from the DOM as it is no longer needed
          if (cache.name.includes(name)) {
            img.removeEventListener('load', this.loadedListener);
            // removeEventListener.bind(img, 'load', this.loadedListener);
            img.remove();
          }
        });
        // SSR
        if (isPlatformServer(this.platform)) {
          // if SSR load svgs on server => ends up in DOM cache and ships to the client
          this.registry.fetchSvg(name);
        }
        // CSR
        else {
          // Activate the lazy loading hack
          // Loading is triggered in the template over loading="lazy" and onload
          // Than the same image is fetched over fromFetch and rendered as SVG. (This will result in a cache hit for this svg)
          //
          // If the img is present activate it
          img && img.style.setProperty('display', 'block');
        }
        onCleanup(() => {
          sub.unsubscribe();
          if (img) {
            img.removeEventListener('load', this.loadedListener);
          }
        });
      }, {
        injector: this.injector
      });
    }
    ngOnDestroy() {
      this.element.nativeElement.querySelector('img')?.removeEventListener('load', this.loadedListener);
    }
    static {
      this.ɵfac = function FastSvgComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FastSvgComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: FastSvgComponent,
        selectors: [["fast-svg"]],
        inputs: {
          name: [1, "name"],
          size: [1, "size"],
          width: [1, "width"],
          height: [1, "height"]
        },
        decls: 2,
        vars: 0,
        consts: [[1, "fast-svg"]],
        template: function FastSvgComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵnamespaceSVG();
            i0.ɵɵelementStart(0, "svg", 0);
            i0.ɵɵelement(1, "use");
            i0.ɵɵelementEnd();
          }
        },
        styles: ["[_nghost-%COMP%]{display:contents}.fast-svg[_ngcontent-%COMP%]{margin:3px;contain:content;content-visibility:auto}"],
        changeDetection: 0
      });
    }
  }
  return FastSvgComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @description
 * Use this function to register the FastSvg providers in your application.
 *
 * @param options {FastSvgProviderOptions} - The options for the FastSvg providers.
 * @return { Provider[] } - The providers for the FastSvg module.
 *
 * @example
 *
 * ```ts
 * bootstrapApplication(AppComponent, {
 *  providers: [
 *   provideFastSVG({
 *     url: (name: string) => `path/to/svg-assets/${name}.svg`,
 *   })
 * ]});
 * ```
 */
const provideFastSVG = options => {
  const providers = [{
    provide: SvgOptionsToken,
    useValue: {
      url: options.url,
      suspenseSvgString: options.suspenseSvgString || undefined,
      defaultSize: options.defaultSize || undefined
    }
  }];
  if (options.svgLoadStrategy) {
    providers.push({
      provide: SvgLoadStrategy,
      useClass: options.svgLoadStrategy
    });
  }
  return providers;
};

/**
 * @deprecated Use `provideFastSVG` provider function instead
 */
let FastSvgModule = /*#__PURE__*/(() => {
  class FastSvgModule {
    static forRoot(options) {
      return {
        ngModule: FastSvgModule,
        providers: [provideFastSVG(options)]
      };
    }
    static forChild() {
      return {
        ngModule: FastSvgModule,
        providers: []
      };
    }
    static {
      this.ɵfac = function FastSvgModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FastSvgModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: FastSvgModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return FastSvgModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// tokens

/**
 * Generated bundle index. Do not edit.
 */

export { FastSvgComponent, FastSvgModule, SvgLoadStrategy, SvgLoadStrategyImpl, SvgOptionsToken, SvgRegistry, provideFastSVG };
